import React, { useState } from 'react';
import Header from "../Header";
import { useDashboard, useDashboardData } from "../../contexts/DashboardContext";
import type { Tab } from "@/services/DashboardService";
import KeyMetricsTab from '@/components/tabs/KeyMetricsTab';
import AgentsTab from '@/components/tabs/AgentsTab';
import { DASHBOARD_TABS } from '@/services/DashboardService';
import QueueTab from '@/components/tabs/QueueTab';
import UsersTab from '@/components/tabs/UsersTab';
import DirectCallTab from '@/components/tabs/DirectCallTab';
import TrendsTab from '@/components/tabs/TrendsTab';
import { useVersion } from '@/contexts/VersionContext';

const GeneralLayout: React.FC = () => {
  const [showAddWidget, setShowAddWidget] = useState(false);
  const { currentWidgetSettings, closeWidgetSettings, theme } = useDashboard();
  const { current } = useDashboardData();
  const { fetchLatestVersionAndApply } = useVersion();

  // Find the first visible tab from current dashboard
  const firstTabValue = React.useMemo(() => {
    if (!current || !current.tabs) return "";
    const firstTab = current.tabs.find((tab: Tab) => !tab.hidden);
    return firstTab ? firstTab.name : "";
  }, [current]);

  const [activeTab, setActiveTab] = useState(firstTabValue);

  // Update activeTab if current changes
  React.useEffect(() => {
    if (firstTabValue) setActiveTab(firstTabValue);
  }, [firstTabValue]);

  const handleOpenAddWidget = () => {
    if (currentWidgetSettings) {
      closeWidgetSettings();
    }
    setShowAddWidget(true);
  };

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    // Auto scroll to top when switching tabs
    const container = document.querySelector('.overflow-auto');
    if (container && typeof container.scrollTo === 'function') {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Check for new version when switching tabs
    fetchLatestVersionAndApply();
  };

  return (
    <div className={`flex h-screen ${theme === 'dark' ? 'bg-[#18181c]' : 'bg-gray-100'}`}>
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          title="General Dashboard"
          onAddWidget={handleOpenAddWidget}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        {/* Static (non-scrolling) spacer so scrolled content can never paint into the
            gap between Header and the sticky filter bar below — unlike padding-top on
            the scroll container itself, which scrolled content can still bleed into. */}
        <div className={`h-4 shrink-0 ${theme === 'dark' ? 'bg-[#0C3451]' : 'bg-white'}`} />
        <div className={`relative w-full h-full pb-6 px-4 ${theme === 'dark' ? 'bg-[#0C3451]' : 'bg-white'} overflow-auto`}>
          {activeTab === DASHBOARD_TABS.KEY_METRICS && (
            <KeyMetricsTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.QUEUE && (
            <QueueTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.AGENT_PERFORMANCE && (
            <AgentsTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.DIRECT_CALL && (
            <DirectCallTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.TRENDS && (
            <TrendsTab isGeneralMode={true} />
          )}
          {activeTab === DASHBOARD_TABS.USERS && (
            <UsersTab isGeneralMode={true} />
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralLayout;
